'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Send from './ui/Send'
import { SplitText } from './ui/SplitText'
import Send2 from './ui/Send2'
import emailjs from 'emailjs-com'
import avatar from '../assets/contactAvatar.png'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: ''
  })

  const validateForm = () => {
    let newErrors = { name: '', email: '', message: '' }
    let isValid = true

    if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters"
      isValid = false
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
      isValid = false
    }

    if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters"
      isValid = false
    }

    setErrors(newErrors)

    if (!isValid) {
      toast.error("Please fix the errors in the form")
    }
    return isValid
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    // Send email using EmailJS
    const serviceId = 'service_hsyh2eg' // Replace with your EmailJS service ID
    const templateId = 'template_pprp4to' // Replace with your EmailJS template ID
    const userId = 'riDqj8tejwHeYiBlM' // Replace with your EmailJS user ID

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
    }

    try {
      const response = await emailjs.send(serviceId, templateId, templateParams, userId)
      if (response.status === 200) {
        toast.success("Message sent successfully!")
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        })
        setErrors({ name: '', email: '', message: '' })
      } else {
        toast.error("Error sending message. Please try again.")
      }
    } catch (error) {
      toast.error("Network error. Please try again later.")
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // GSAP Animation for contact avatar
  useEffect(() => {
    gsap.fromTo(
      '.contact-avatar-img',
      {
        opacity: 0,
        scale: 0,
        rotation: -180,
      },
      {
        opacity: 1,
        scale: 1,
        rotation: 0,
        duration: 1.2,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.contact-title-container',
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section id='contact' className="py-20 overflow-hidden relative dark:bg-zinc-950 bg-zinc-100 px-4 lg:px-32 transition-colors duration-300">

      <div className=' w-full h-full'>
        <div
          className="absolute top-0 left-0 w-72 h-72 opacity-20 dark:opacity-10 text-muted-foreground"
          style={{
            backgroundImage: `
      linear-gradient(to right, currentColor 1.5px, transparent 1.5px),
      linear-gradient(to bottom, currentColor 1.5px, transparent 1.5px)
    `,
            backgroundSize: "60px 60px"
          }}
        />

      </div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.5, ease: "backOut" }}
        className="absolute -bottom-20 -right-20 w-52 h-52 rounded-full bg-zinc-600"
      ></motion.div>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, delay: 0.7, ease: "backOut" }}
        className="absolute -bottom-28 -right-28 w-72 h-72 rounded-full bg-opacity-20 bg-zinc-300"
      ></motion.div>

      <div className="w-full flex justify-between flex-col md:flex-row mx-auto items-start gap-6">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="contact-title-container w-full md:w-6/12"
        >
          <div className='flex flex-row justify-start items-center'>
            <SplitText text="Let's" className="text-zinc-900 dark:text-zinc-50 text-5xl sm:text-5xl md:text-6xl lg:text-8xl font-bold leading-tight mr-1" delay={50} />
            <div>
              <img src={avatar} alt="" className='contact-avatar-img w-16 sm:w-20 md:w-28 lg:w-40 z-10' />
            </div>
          </div>
          <SplitText text="Connnect!" className="text-zinc-900 dark:text-zinc-50 -mt-4 sm:-mt-6 md:-mt-8 text-5xl sm:text-5xl md:text-6xl lg:text-8xl font-bold leading-tight mb-4 lg:mb-6" delay={50} />
          <p className="text-zinc-700 dark:text-zinc-400 text-base sm:text-lg lg:text-2xl my-2 lg:mb-2">
            Got a question or an idea? I'm just a message away let's chat and bring it to life!
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          onSubmit={handleSubmit}
          noValidate
          className="w-full md:w-5/12 space-y-8"
        >
          <div className="flex gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className={`peer w-full border-b-2 ${errors.name ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} bg-transparent py-2 px-1 text-zinc-900 dark:text-zinc-50 placeholder-transparent focus:border-zinc-700 dark:focus:border-zinc-300 focus:outline-none transition-all`}
                placeholder="Name"
              />
              <label
                className={`absolute left-1 -top-5 text-sm transition-all 
                  ${errors.name ? 'text-red-500' : 'text-zinc-500 dark:text-zinc-400'}
                  peer-placeholder-shown:text-base peer-placeholder-shown:text-zinc-500 dark:peer-placeholder-shown:text-zinc-500 peer-placeholder-shown:top-2 
                  peer-focus:-top-5 peer-focus:text-zinc-500 duration-700  dark:peer-focus:text-zinc-400 peer-focus:text-sm`}
              >
                Name*
              </label>
              {errors.name && (
                <p className="absolute left-1 top-full text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>
            <div className="relative flex-1">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="peer w-full border-b-2 border-zinc-300 dark:border-zinc-700 bg-transparent py-2 px-1 text-zinc-900 dark:text-zinc-50 placeholder-transparent focus:border-zinc-700 dark:focus:border-zinc-300   focus:outline-none transition-all"
                placeholder="Phone"
              />
              <label
                className="absolute left-1 -top-5 text-zinc-500 dark:text-zinc-400 text-sm transition-all 
                  peer-placeholder-shown:text-base peer-placeholder-shown:text-zinc-500 dark:peer-placeholder-shown:text-zinc-500 peer-placeholder-shown:top-2 
                  peer-focus:-top-5 peer-focus:text-zinc-500 duration-700  dark:peer-focus:text-zinc-400 peer-focus:text-sm"
              >
                Phone (optional)
              </label>
            </div>
          </div>

          <div className="relative">
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={`peer w-full border-b-2 ${errors.email ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} bg-transparent py-2 px-1 text-zinc-900 dark:text-zinc-50 placeholder-transparent focus:border-zinc-700 dark:focus:border-zinc-300 focus:outline-none duration-700   transition-all`}
              placeholder="Email"
            />
            <label
              className={`absolute left-1 -top-5 text-sm transition-all 
                ${errors.email ? 'text-red-500' : 'text-zinc-500 dark:text-zinc-400'}
                peer-placeholder-shown:text-base peer-placeholder-shown:text-zinc-500 dark:peer-placeholder-shown:text-zinc-500 peer-placeholder-shown:top-2 
                peer-focus:-top-5 duration-700   peer-focus:text-zinc-500 dark:peer-focus:text-zinc-400 peer-focus:text-sm`}
            >
              Email*
            </label>
            {errors.email && (
              <p className="absolute left-1 top-full text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          <div className="relative">
            <textarea
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              rows={2}
              className={`peer w-full border-b-2 duration-700 ${errors.message ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} bg-transparent py-2 px-1 text-zinc-900 dark:text-zinc-50 placeholder-transparent focus:border-zinc-700 dark:focus:border-zinc-300 focus:outline-none transition-all resize-none`}
              placeholder="Message"
            />
            <label
              className={`absolute left-1 -top-5 text-sm transition-all 
                ${errors.message ? 'text-red-500' : 'text-zinc-500 dark:text-zinc-400'}
                peer-placeholder-shown:text-base duration-700   peer-placeholder-shown:text-zinc-500 dark:peer-placeholder-shown:text-zinc-500 peer-placeholder-shown:top-2 
                peer-focus:-top-5 peer-focus:text-zinc-500 dark:peer-focus:text-zinc-400 peer-focus:text-sm`}
            >
              Message*
            </label>
            {errors.message && (
              <p className="absolute left-1 top-full text-xs text-red-500 mt-1">{errors.message}</p>
            )}
          </div>

          <div className="text-sm text-zinc-500 dark:text-zinc-400">
            By submitting this form, you agree to our{' '}
            <div className="relative group inline-block">
              <a href="/privacy-policy" className="text-zinc-700 hover:text-zinc-500 dark:text-zinc-300 dark:hover:text-zinc-200 transition-colors">
                Privacy Policy
              </a>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-zinc-800 text-zinc-50 dark:bg-zinc-50 dark:text-zinc-800 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none mb-1 whitespace-nowrap">
                Our commitment to protecting your data
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className=" dark:hidden block transition-colors"
          >
            <Send />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="hidden dark:block transition-colors"
          >
            <Send2 />
          </motion.button>
        </motion.form>
      </div>
      <ToastContainer />
    </section>
  )
}

export default ContactForm

